import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config'; 
import { collection, query, doc, deleteDoc, onSnapshot, orderBy } from 'firebase/firestore';

import ModalEndoso from '../../../ui/ModalEndoso'; 
import ModalNotificacion from '../../../ui/ModalNotificacion'; 
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 
import { getEndosoStatusChip, getPersonaChipClass } from '../../../../utils/styles';

// --- ICONOS ---
const IconShield = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;

/**
 * EndososTab - Gestión de endosos con sincronización en tiempo real (Real-time).
 */
const EndososTab = ({ poliza, client }) => {
    const [endosos, setEndosos] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingEndoso, setEditingEndoso] = useState(null);

    const [expandedEndosoId, setExpandedEndosoId] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, endosoToDelete: null });

    const [showSensitiveData, setShowSensitiveData] = useState({});

    const polizaId = poliza.id;
    const clientId = client.id;

    // --- 1. LISTENER EN TIEMPO REAL (onSnapshot) ---
    useEffect(() => {
        if (!polizaId || !clientId) return;

        setLoading(true);
        const endososRef = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`);
        
        // Ordenamos por fechaCarga descendente directamente desde la consulta
        const q = query(endososRef); 

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Filtramos y ordenamos localmente para asegurar compatibilidad con formatos legacy de fecha
            const filtered = allData
                .filter(e => e.tipoGestion !== 'Refacturacion')
                .sort((a, b) => {
                    const dateA = a.fechaCarga || '';
                    const dateB = b.fechaBarga || '';
                    return dateB.localeCompare(dateA);
                });

            setEndosos(filtered);
            setLoading(false);
        }, (err) => {
            console.error("Error en listener de endosos:", err);
            setNotification({ show: true, message: 'Error al sincronizar la lista en tiempo real.', type: 'error' });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [polizaId, clientId]);

    const confirmDelete = (endoso) => {
        setDeletionConfirm({ show: true, endosoToDelete: endoso });
    };
    
    const handleDelete = async () => {
        const endosoId = deletionConfirm.endosoToDelete.id;
        const endosoTramite = deletionConfirm.endosoToDelete.tramite || endosoId.substring(0, 5);
        setDeletionConfirm({ show: false, endosoToDelete: null });

        try {
            const endosoRef = doc(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`, endosoId);
            await deleteDoc(endosoRef);
            // No es necesario llamar a fetchEndosos(), el listener lo hará solo
            setNotification({ show: true, message: `El endoso N° ${endosoTramite} ha sido eliminado correctamente.`, type: 'success' });
        } catch (error) {
            console.error("Error al eliminar el endoso:", error);
            setNotification({ show: true, message: 'Ocurrió un error al intentar eliminar el endoso.', type: 'error' });
        }
    };

    const handleModalSuccess = (msg, type = 'success') => {
        // No es necesario llamar a fetchEndosos(), el listener lo hará solo
        setEditingEndoso(null);
        setShowModal(false);
        setNotification({ show: true, message: msg, type: type }); 
    };

    const formatDate = (dateString) => {
        if(!dateString) return '-';
        if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts = dateString.split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
    };
    
    const toggleRowExpansion = (endosoId) => {
        setExpandedEndosoId(expandedEndosoId === endosoId ? null : endosoId);
    };

    const toggleSensitiveData = (e, id) => {
        e.stopPropagation();
        setShowSensitiveData(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const PagoDetails = ({ endoso }) => {
        const isCBU = String(endoso.formaPago || '').toUpperCase() === 'CBU';
        const visible = showSensitiveData[endoso.id];
        const valorPrincipal = isCBU ? (endoso.numeroCbu || endoso.cbu) : (endoso.numeroTarjeta || endoso.numero);

        const formatMasked = (val, type) => {
            if (!val) return '****';
            const stringVal = String(val).trim();
            if (visible) return stringVal;
            return type === 'CBU' 
                ? `**** **** **** ${stringVal.slice(-6)}` 
                : `**** **** **** ${stringVal.slice(-4)}`;
        };

        return (
            <div className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm mb-4 animate-fade-in text-left max-w-2xl">
                <div className="flex items-center justify-between border-b border-slate-50 pb-2 mb-3">
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${isCBU ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                            {isCBU ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                            ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                            )}
                        </div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nuevo Método de Pago</span>
                    </div>
                    <button 
                        onClick={(e) => toggleSensitiveData(e, endoso.id)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50 transition-colors"
                    >
                        {visible ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L5.93 5.93m4.89 4.89l-1.12 1.12m0 0a3 3 0 004.242 4.243l3.025 3.025M12 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {isCBU ? (
                        <>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase">Banco</p>
                                <p className="text-sm font-bold text-slate-800">{endoso.banco || '-'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-[9px] font-black text-slate-400 uppercase">CBU</p>
                                <p className={`text-sm font-mono font-black ${visible ? 'text-blue-700' : 'text-slate-600'}`}>
                                    {formatMasked(valorPrincipal, 'CBU')}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase">Marca / Banco</p>
                                <p className="text-sm font-bold text-slate-800 uppercase">{endoso.marca || 'S/D'} - {endoso.banco || 'S/D'}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase">Vencimiento</p>
                                <p className="text-sm font-bold text-slate-800">{endoso.vencimientoTarjeta || endoso.vencimiento || '-'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-[9px] font-black text-slate-400 uppercase">Número de Tarjeta</p>
                                <p className={`text-sm font-mono font-black ${visible ? 'text-blue-700' : 'text-slate-600'}`}>
                                    {formatMasked(valorPrincipal, 'CARD')}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
            
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500"></div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase">Endosos y Modificaciones</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Historial de gestiones de la póliza</p>
                </div>
                <button 
                    onClick={() => { setEditingEndoso(null); setShowModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                    Cargar Endoso
                </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1000px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                        <tr>
                            <th className="p-4 w-12 text-center"></th>
                            <th className="p-4 w-28 whitespace-nowrap">Fecha Carga</th>
                            <th className="p-4 w-48">Tipo Gestión</th>
                            <th className="p-4 w-40 text-center">Responsable</th>
                            <th className="p-4 w-32 text-center">Trámite N°</th>
                            <th className="p-4 w-32 text-center">Estado del Endoso</th>
                            <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="7" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando endosos...</td></tr>
                        ) : endosos.length === 0 ? (
                            <tr><td colSpan="7" className="p-16 text-center text-slate-400 italic font-medium">No hay endosos registrados.</td></tr>
                        ) : (
                            endosos.map(endoso => (
                                <React.Fragment key={endoso.id}>
                                    <tr 
                                        onClick={() => toggleRowExpansion(endoso.id)}
                                        className={`hover:bg-slate-50 transition-colors cursor-pointer group ${expandedEndosoId === endoso.id ? 'bg-blue-50/40' : ''}`}
                                    >
                                        <td className="p-4 text-center">
                                            <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${expandedEndosoId === endoso.id ? 'rotate-90 text-blue-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                                        </td>
                                        <td className="p-4 font-bold text-slate-500 tabular-nums whitespace-nowrap">
                                            {formatDate(endoso.fechaCarga)}
                                        </td>
                                        <td className="p-4 font-bold text-blue-700 uppercase tracking-tight">
                                            {endoso.tipoGestion || '-'}
                                        </td>
                                        <td className="p-4 text-center whitespace-nowrap">
                                            {endoso.quienGestiono ? (
                                                <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(endoso.quienGestiono)}`}>
                                                    {endoso.quienGestiono}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="p-4 text-center font-bold text-slate-800">
                                            {endoso.tramite || '-'}
                                        </td>
                                        <td className="p-4 text-center whitespace-nowrap">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${getEndosoStatusChip(endoso.estadoGestion)}`}>
                                                {endoso.estadoGestion}
                                            </span>
                                        </td>
                                        <td className={`p-4 text-right pr-6 sticky right-0 z-10 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)] ${expandedEndosoId === endoso.id ? 'bg-[#f8faff]' : 'bg-white group-hover:bg-slate-50'}`}>
                                            <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                                <button onClick={(e) => { e.stopPropagation(); setEditingEndoso(endoso); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                <button onClick={(e) => { e.stopPropagation(); confirmDelete(endoso); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                            </div>
                                        </td>
                                    </tr>
                                    
                                    {expandedEndosoId === endoso.id && (
                                        <tr className="bg-slate-50/50 border-b border-slate-100 animate-slide-down">
                                            <td colSpan="7" className="px-12 py-6 text-left">
                                                <div className="flex flex-col gap-6">
                                                    {endoso.tipoGestion === 'Cambio de Método de Pago' && (
                                                        <PagoDetails endoso={endoso} />
                                                    )}

                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2 mb-1"><IconShield /> Observaciones de la gestión</h4>
                                                        <div className="pl-4 border-l-4 border-blue-100">
                                                            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                                                                "{endoso.observaciones || 'No se registraron observaciones adicionales para esta gestión.'}"
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                .animate-slide-down { animation: slideDown 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <ModalEndoso 
                show={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={handleModalSuccess}
                poliza={poliza}
                clientData={client}
                initialData={editingEndoso}
            />

            <ModalNotificacion
                show={notification.show}
                onClose={() => setNotification({ show: false, message: '', type: 'success' })}
                message={notification.message}
                type={notification.type}
            />
            
            <ModalConfirmacion
                show={deletionConfirm.show}
                onClose={() => setDeletionConfirm({ show: false, endosoToDelete: null })}
                onConfirm={handleDelete}
                title="Eliminar Endoso"
                message={`¿Estás seguro de que deseas eliminar el endoso N° ${deletionConfirm.endosoToDelete?.tramite || deletionConfirm.endosoToDelete?.id?.substring(0, 5) || 'seleccionado'}? Esta acción es irreversible.`}
            />
        </div>
    );
};

export default EndososTab;